import React from 'react';
import { useTranslation } from 'react-i18next';
import { Info } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { LoadingButton } from '@/components/common/LoadingButton';
import { Button } from '@/components/ui/button';
import { useDocumentCategories } from '@hooks/useDocumentCategories';
import { MetadataBasicSection } from './metadata/MetadataBasicSection';
import { MetadataSecuritySection } from './metadata/MetadataSecuritySection';
import { MetadataTagsSection } from './metadata/MetadataTagsSection';
import {
  useDocumentMetadataForm,
  type MetadataFormData,
} from './metadata/useDocumentMetadataForm';

interface DocumentMetadataFormProps {
  onSubmit: (metadata: MetadataFormData) => void;
  fileCount: number;
  /** Lets the wizard warn before a step change unmounts this form and loses its input. */
  onDirtyChange?: (dirty: boolean) => void;
}

const DocumentMetadataForm: React.FC<DocumentMetadataFormProps> = ({
  onSubmit,
  fileCount,
  onDirtyChange,
}) => {
  const { t } = useTranslation();
  const categoryChoices = useDocumentCategories();
  const acceptsCategory = (code: string) => categoryChoices.status === 'ready' && (
    categoryChoices.categories.some((category) => category.code === code) ||
    (categoryChoices.categories.length === 0 && code === 'general')
  );
  const { formData, errors, isDirty, update, updatePermission, addTag, handleSubmit } =
    useDocumentMetadataForm((values) => {
      if (acceptsCategory(values.category)) onSubmit(values);
    });

  React.useEffect(() => {
    if (categoryChoices.status !== 'ready') update('category', '');
  }, [categoryChoices.status, categoryChoices.tenantId, update]);

  React.useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <Alert variant="info">
        <Info className="size-4" strokeWidth={2} />
        <AlertDescription className="revy-cjk text-sm leading-[1.8]">
          {t('metadata.metadataNote', {
            count: fileCount,
            files: fileCount === 1 ? t('metadata.file') : t('metadata.files'),
          })}
        </AlertDescription>
      </Alert>

      {categoryChoices.status === 'loading' && (
        <p role="status" className="text-sm text-muted-foreground">{t('metadata.categoriesLoading')}</p>
      )}
      {categoryChoices.status === 'error' && (
        <Alert variant="destructive">
          <AlertDescription>
            {t('metadata.categoriesLoadFailed')}
            <Button type="button" variant="outline" size="sm" onClick={categoryChoices.retry} className="ml-3">
              {t('common.retry')}
            </Button>
          </AlertDescription>
        </Alert>
      )}
      {categoryChoices.status === 'ready' && categoryChoices.categories.length === 0 && (
        <p role="status" className="text-sm text-muted-foreground">{t('metadata.categoriesEmpty')}</p>
      )}
      <MetadataBasicSection
        formData={formData}
        errors={errors}
        onChange={update}
        categories={categoryChoices.categories}
        categoriesLoading={categoryChoices.status !== 'ready'}
      />

      <MetadataTagsSection
        tags={formData.tags}
        error={errors.tags}
        onTagsChange={(tags) => update('tags', tags)}
        onAddTag={addTag}
      />

      <MetadataSecuritySection
        formData={formData}
        onClassificationChange={(value) => update('classification', value)}
        onPermissionChange={updatePermission}
      />

      <div className="flex justify-end">
        <LoadingButton type="submit" size="lg" disabled={!acceptsCategory(formData.category)}>
          {t('metadata.continueToUpload')}
        </LoadingButton>
      </div>
    </form>
  );
};

export default DocumentMetadataForm;
